import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { FaTruck, FaPen, FaAddressBook, FaToggleOn, FaToggleOff, FaDatabase } from 'react-icons/fa';
import { TabHub } from '../../../components/Hubs/TabHub';
import { ifsService, clearIFSCache } from '../../../services/ifsService';
import { apiClient } from '../../../utils/apiClient';
import { getActiveIFSAccountId, setActiveIFSAccountId } from '../../../utils/ifsAccount';
import mockDataService from '../../../services/mockDataService';
import { featureFlagService } from '../../../services/featureFlagService';
import ShipmentsTab from './ShipmentsTab';
import CreateShipmentTab from './CreateShipmentTab';
import AddressBookTab from './AddressBookTab';
import { MockModeToggle, MockModeBadge, MockInfoBanner, ConnectionDot, ConnectionStatus, AccountSelector, } from './IFSShippingDashboard.styles';
const IFSShippingDashboard = () => {
    const [activeTab, setActiveTab] = useState('shipments');
    const [isMockMode, setIsMockMode] = useState(mockDataService.isShippingMockMode());
    const [showMockInfo, setShowMockInfo] = useState(false);
    const [isConnected, setIsConnected] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [selectedAccountId, setSelectedAccountId] = useState(getActiveIFSAccountId());
    const [accountsLoaded, setAccountsLoaded] = useState(false);
    const [labelCreationOn, setLabelCreationOn] = useState(null);
    const [flagBusy, setFlagBusy] = useState(false);
    useEffect(() => {
        featureFlagService.get('ifs_label_creation')
            .then(res => setLabelCreationOn(res.ok && res.data?.success ? res.data.enabled : false))
            .catch(() => setLabelCreationOn(false));
    }, []);
    const handleToggleLabelCreation = async () => {
        if (labelCreationOn === null || flagBusy)
            return;
        const next = !labelCreationOn;
        if (next && !window.confirm('Enable LIVE label creation? Labels generated while this is on are real and billable.'))
            return;
        setFlagBusy(true);
        try {
            const res = await featureFlagService.set('ifs_label_creation', next);
            if (res.ok && res.data?.success)
                setLabelCreationOn(res.data.enabled);
            else
                alert(res.data && res.data.error ? res.data.error : 'Only platform/super admins can toggle label creation');
        }
        finally {
            setFlagBusy(false);
        }
    };
    useEffect(() => {
        if (isMockMode) {
            setAccountsLoaded(true);
            return;
        }
        apiClient.get('/ifs-accounts').then(res => {
            const list = res.ok && res.data?.success ? res.data.accounts : [];
            setAccounts(list);
            const stored = getActiveIFSAccountId();
            const exists = stored && list.some(a => a.id === stored);
            if (!exists) {
                const def = list.find(a => a.isDefault) || list[0];
                const newId = def ? def.id : null;
                setActiveIFSAccountId(newId);
                setSelectedAccountId(newId);
            }
            setAccountsLoaded(true);
        }).catch(() => setAccountsLoaded(true));
    }, [isMockMode]);
    useEffect(() => {
        if (isMockMode || !accountsLoaded) {
            setIsConnected(null);
            return;
        }
        setIsConnected(null);
        ifsService.getCountryState().then(res => {
            const connected = res.ok && res.data?.success && (res.data?.data?.status === '1' || res.data?.data?.status === 1);
            setIsConnected(connected ?? false);
        }).catch(() => setIsConnected(false));
    }, [isMockMode, accountsLoaded, selectedAccountId]);
    const handleToggleMockMode = () => {
        const newMode = mockDataService.toggleShippingMockMode();
        setIsMockMode(newMode);
        window.location.reload();
    };
    const handleAccountChange = (e) => {
        const newId = e.target.value || null;
        setActiveIFSAccountId(newId);
        setSelectedAccountId(newId);
        clearIFSCache();
    };
    const tabs = [
        {
            id: 'shipments',
            label: 'Shipments',
            description: 'Track & Manage',
            icon: _jsx(FaTruck, {}),
            component: _jsx(ShipmentsTab, { isMockMode: isMockMode }),
        },
        {
            id: 'create',
            label: 'Create Shipment',
            description: 'New Draft',
            icon: _jsx(FaPen, {}),
            component: _jsx(CreateShipmentTab, { isMockMode: isMockMode }),
        },
        {
            id: 'addressbook',
            label: 'Address Book',
            description: 'Senders & Recipients',
            icon: _jsx(FaAddressBook, {}),
            component: _jsx(AddressBookTab, { isMockMode: isMockMode }),
        },
    ];
    const showSelector = !isMockMode && accounts.length > 1;
    const headerActions = (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }, children: [showSelector && (_jsx(AccountSelector, { value: selectedAccountId || '', onChange: handleAccountChange, title: "Active IFS account", children: accounts.map(a => (_jsxs("option", { value: a.id, children: [a.label, " (", a.id, ")"] }, a.id))) })), _jsxs(ConnectionStatus, { children: [isMockMode ? 'Mock Mode' : 'IFS API', _jsx(ConnectionDot, { "$connected": isMockMode ? false : isConnected === true }), !isMockMode && (isConnected === null ? 'Checking...' : isConnected ? 'Connected' : 'Disconnected')] }), _jsxs(MockModeToggle, { onClick: handleToggleMockMode, active: isMockMode, children: [isMockMode ? _jsx(FaToggleOn, {}) : _jsx(FaToggleOff, {}), _jsx("span", { children: isMockMode ? 'Mock ON' : 'Mock OFF' }), _jsx(FaDatabase, {})] }), !isMockMode && labelCreationOn !== null && (_jsxs(MockModeToggle, { onClick: handleToggleLabelCreation, active: labelCreationOn, title: labelCreationOn ? 'Live label creation is ON — labels are real and billable' : 'Label creation is OFF — Generate Label is hidden', style: labelCreationOn ? { borderColor: '#00c853', color: '#00c853' } : undefined, children: [labelCreationOn ? _jsx(FaToggleOn, {}) : _jsx(FaToggleOff, {}), _jsx("span", { children: flagBusy ? '…' : `Labels ${labelCreationOn ? 'ON' : 'OFF'}` })] }))] }));
    const infoBanner = isMockMode && showMockInfo ? (_jsxs(MockInfoBanner, { children: [_jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }, children: [_jsx("h3", { style: { margin: 0, color: '#059669', fontSize: '1.1rem', fontWeight: 700 }, children: "IFS Shipping Demo Mode" }), _jsx("button", { onClick: () => setShowMockInfo(false), style: { background: 'none', border: 'none', fontSize: '1.25rem', color: '#059669', cursor: 'pointer' }, children: "x" })] }), _jsx("p", { style: { color: '#047857', fontSize: '0.875rem', margin: 0 }, children: "15 simulated shipments, 5 sender addresses, 8 recipients, mock cost calculations. All CRUD operations are functional in mock mode." })] })) : (isMockMode ? (_jsx(MockModeBadge, { onClick: () => setShowMockInfo(!showMockInfo), children: "Demo Mode Active - Click for info" })) : null);
    return (_jsx(TabHub, { title: "IFS Shipping Dashboard", subtitle: "Manage shipments, create drafts, and track deliveries through IFS Inforsure FedEx integration", activeTab: activeTab, tabs: tabs, onTabChange: (id) => setActiveTab(id), headerActions: headerActions, infoBanner: infoBanner }));
};
export default IFSShippingDashboard;
